// src/config/standards/iste.js

// ISTE Standards for Students (aligned, paraphrased — not verbatim).
// Domains:
//  1) Empowered Learner (EL)
//  2) Digital Citizen (DC)
//  3) Knowledge Constructor (KC)
//  4) Innovative Designer (ID)
//  5) Computational Thinker (CT)
//  6) Creative Communicator (CC)
//  7) Global Collaborator (GC)
//
// Grade bands used by your app:
//  * K–2     -> used for Kindergarten, 1st Grade, 2nd Grade
//  * 3–5     -> used for 3rd, 4th, 5th Grade
//  * 6–8     -> used for 6th, 7th, 8th Grade
//  * High School -> per-domain clusters "HS—9–10" and "HS—11–12"
//
// Coding convention:
//   id:   `ISTE (Students)-<Band>-ISTE.<DOM>.<Band>.<indicator>`
//   code: `ISTE.<DOM>.<Band>.<indicator>`
// where <indicator> is a, b, c (three per domain per band).

const isteItem = (band, code, description) => ({
  id: `ISTE (Students)-${band}-${code}`,
  code,
  description,
});

// ----------------------------------
// K–2
// ----------------------------------
const K2 = {
  "Empowered Learner": [
    isteItem("K–2", "ISTE.EL.K-2.a", "With guidance, set a simple learning goal and use a digital tool to work toward it."),
    isteItem("K–2", "ISTE.EL.K-2.b", "Choose from a few teacher-selected apps or features that help you learn best."),
    isteItem("K–2", "ISTE.EL.K-2.c", "Tell what you learned and how a tool helped you (voice, picture, or short sentence)."),
  ],
  "Digital Citizen": [
    isteItem("K–2", "ISTE.DC.K-2.a", "Follow class rules for safe, kind, and responsible technology use."),
    isteItem("K–2", "ISTE.DC.K-2.b", "Ask an adult before sharing information about yourself online."),
    isteItem("K–2", "ISTE.DC.K-2.c", "Respect others’ work; only copy or use things your teacher says are OK."),
  ],
  "Knowledge Constructor": [
    isteItem("K–2", "ISTE.KC.K-2.a", "Use a curated set of websites/books to find pictures or facts about a topic."),
    isteItem("K–2", "ISTE.KC.K-2.b", "Sort or group information with help (e.g., animals vs. plants)."),
    isteItem("K–2", "ISTE.KC.K-2.c", "Say where you found information (title, site name, or helper)."),
  ],
  "Innovative Designer": [
    isteItem("K–2", "ISTE.ID.K-2.a", "Try different ideas to solve a simple problem with digital or physical tools."),
    isteItem("K–2", "ISTE.ID.K-2.b", "Build a basic model or drawing; tell what works and what you might change."),
    isteItem("K–2", "ISTE.ID.K-2.c", "Improve your project after testing or feedback from a classmate/teacher."),
  ],
  "Computational Thinker": [
    isteItem("K–2", "ISTE.CT.K-2.a", "Describe steps to do a task in order (first, next, last)."),
    isteItem("K–2", "ISTE.CT.K-2.b", "Spot simple patterns (repeat, big/small, up/down) in stories, songs, or code blocks."),
    isteItem("K–2", "ISTE.CT.K-2.c", "Use a beginner coding or sequencing tool to make a character move in order."),
  ],
  "Creative Communicator": [
    isteItem("K–2", "ISTE.CC.K-2.a", "Choose a way to show your ideas (drawing, photo, voice note, or short text)."),
    isteItem("K–2", "ISTE.CC.K-2.b", "Add a title, label, or caption so others understand your work."),
    isteItem("K–2", "ISTE.CC.K-2.c", "Share your project with the class and listen to peers’ ideas."),
  ],
  "Global Collaborator": [
    isteItem("K–2", "ISTE.GC.K-2.a", "Work with a partner in a shared space (take turns, add, and comment kindly)."),
    isteItem("K–2", "ISTE.GC.K-2.b", "Use class-approved tools to say hello or share a picture with another class."),
    isteItem("K–2", "ISTE.GC.K-2.c", "Describe how classmates can help one another learn online and in person."),
  ],
};

// ----------------------------------
// 3–5
// ----------------------------------
const G3_5 = {
  "Empowered Learner": [
    isteItem("3–5", "ISTE.EL.3-5.a", "Set a learning goal, pick appropriate digital tools, and reflect on your progress."),
    isteItem("3–5", "ISTE.EL.3-5.b", "Customize settings (read-aloud, zoom, organization) to support how you learn best."),
    isteItem("3–5", "ISTE.EL.3-5.c", "Seek feedback and use it to improve your product or process."),
  ],
  "Digital Citizen": [
    isteItem("3–5", "ISTE.DC.3-5.a", "Protect personal information; use strong passwords and sign out of shared devices."),
    isteItem("3–5", "ISTE.DC.3-5.b", "Explain why it’s important to credit creators and follow usage permissions."),
    isteItem("3–5", "ISTE.DC.3-5.c", "Demonstrate respectful behavior in digital spaces; report unsafe content."),
  ],
  "Knowledge Constructor": [
    isteItem("3–5", "ISTE.KC.3-5.a", "Use keywords and filters to find reliable information from multiple sources."),
    isteItem("3–5", "ISTE.KC.3-5.b", "Evaluate sources for author, purpose, and currency; collect evidence in notes."),
    isteItem("3–5", "ISTE.KC.3-5.c", "Cite sources in a simple format when publishing or presenting."),
  ],
  "Innovative Designer": [
    isteItem("3–5", "ISTE.ID.3-5.a", "Define a problem and criteria; brainstorm and compare solution ideas."),
    isteItem("3–5", "ISTE.ID.3-5.b", "Prototype with digital/physical tools; test and record results."),
    isteItem("3–5", "ISTE.ID.3-5.c", "Iterate to improve performance, clarity, or user experience."),
  ],
  "Computational Thinker": [
    isteItem("3–5", "ISTE.CT.3-5.a", "Decompose a task into steps or modules; plan with flowcharts/storyboards."),
    isteItem("3–5", "ISTE.CT.3-5.b", "Create algorithms in block-based environments; debug common errors."),
    isteItem("3–5", "ISTE.CT.3-5.c", "Collect and organize data; recognize patterns to make predictions."),
  ],
  "Creative Communicator": [
    isteItem("3–5", "ISTE.CC.3-5.a", "Select the best media (text, audio, video, slides) for your audience/purpose."),
    isteItem("3–5", "ISTE.CC.3-5.b", "Apply design basics (contrast, alignment, hierarchy) for clarity."),
    isteItem("3–5", "ISTE.CC.3-5.c", "Revise communication based on peer/teacher feedback."),
  ],
  "Global Collaborator": [
    isteItem("3–5", "ISTE.GC.3-5.a", "Collaborate in shared docs/boards with roles, deadlines, and respectful comments."),
    isteItem("3–5", "ISTE.GC.3-5.b", "Connect with peers or experts using class-approved tools to gather perspectives."),
    isteItem("3–5", "ISTE.GC.3-5.c", "Contribute responsibly to a group product that represents multiple viewpoints."),
  ],
};

// ----------------------------------
// 6–8
// ----------------------------------
const G6_8 = {
  "Empowered Learner": [
    isteItem("6–8", "ISTE.EL.6-8.a", "Set goals, track progress, and adapt strategies using analytics or reflections."),
    isteItem("6–8", "ISTE.EL.6-8.b", "Select and justify digital tools/workflows that optimize your learning."),
    isteItem("6–8", "ISTE.EL.6-8.c", "Use feedback loops and self-assessment to improve outcomes."),
  ],
  "Digital Citizen": [
    isteItem("6–8", "ISTE.DC.6-8.a", "Manage privacy, security, and digital identity across platforms."),
    isteItem("6–8", "ISTE.DC.6-8.b", "Recognize misinformation, bias, and manipulative design; act responsibly."),
    isteItem("6–8", "ISTE.DC.6-8.c", "Respect intellectual property; apply appropriate citations and licenses."),
  ],
  "Knowledge Constructor": [
    isteItem("6–8", "ISTE.KC.6-8.a", "Formulate research questions; use advanced search and databases."),
    isteItem("6–8", "ISTE.KC.6-8.b", "Evaluate credibility and synthesize ideas across diverse sources."),
    isteItem("6–8", "ISTE.KC.6-8.c", "Curate resources and cite with consistent formats."),
  ],
  "Innovative Designer": [
    isteItem("6–8", "ISTE.ID.6-8.a", "Use an iterative design process with constraints, prototypes, and tests."),
    isteItem("6–8", "ISTE.ID.6-8.b", "Gather user feedback; refine for usability and performance."),
    isteItem("6–8", "ISTE.ID.6-8.c", "Reflect on tradeoffs (time, cost, accessibility, sustainability)."),
  ],
  "Computational Thinker": [
    isteItem("6–8", "ISTE.CT.6-8.a", "Design algorithms; debug systematically in block or text-based tools."),
    isteItem("6–8", "ISTE.CT.6-8.b", "Model data with tables and basic logic; automate repetitive steps."),
    isteItem("6–8", "ISTE.CT.6-8.c", "Analyze data for trends; explain limitations and potential bias."),
  ],
  "Creative Communicator": [
    isteItem("6–8", "ISTE.CC.6-8.a", "Produce clear multimedia with purposeful structure and pacing."),
    isteItem("6–8", "ISTE.CC.6-8.b", "Adapt message, tone, and format for different audiences/stakeholders."),
    isteItem("6–8", "ISTE.CC.6-8.c", "Credit collaborators and sources; release with appropriate permissions."),
  ],
  "Global Collaborator": [
    isteItem("6–8", "ISTE.GC.6-8.a", "Plan and manage roles, milestones, and shared artifacts in team projects."),
    isteItem("6–8", "ISTE.GC.6-8.b", "Engage respectfully with diverse partners; resolve conflicts constructively."),
    isteItem("6–8", "ISTE.GC.6-8.c", "Co-create products that integrate varied viewpoints and feedback."),
  ],
};

// ----------------------------------
// HIGH SCHOOL CLUSTERS (per domain)
// ----------------------------------
const HS_9_10 = {
  "Empowered Learner": [
    isteItem("HS—9–10", "ISTE.EL.HS-9-10.a", "Select, justify, and document toolchains that improve efficiency and learning outcomes."),
    isteItem("HS—9–10", "ISTE.EL.HS-9-10.b", "Use analytics and reflection to revise goals and strategies."),
    isteItem("HS—9–10", "ISTE.EL.HS-9-10.c", "Seek and apply expert/peer feedback to iterate products to quality."),
  ],
  "Digital Citizen": [
    isteItem("HS—9–10", "ISTE.DC.HS-9-10.a", "Manage personal data, identities, and security settings across platforms."),
    isteItem("HS—9–10", "ISTE.DC.HS-9-10.b", "Detect misinformation and bias; apply verification strategies."),
    isteItem("HS—9–10", "ISTE.DC.HS-9-10.c", "Apply copyright, fair use, and open licenses appropriately."),
  ],
  "Knowledge Constructor": [
    isteItem("HS—9–10", "ISTE.KC.HS-9-10.a", "Design multi-source research plans using advanced search/databases."),
    isteItem("HS—9–10", "ISTE.KC.HS-9-10.b", "Synthesize conflicting evidence; build defensible claims."),
    isteItem("HS—9–10", "ISTE.KC.HS-9-10.c", "Maintain organized bibliographies and accurate citations."),
  ],
  "Innovative Designer": [
    isteItem("HS—9–10", "ISTE.ID.HS-9-10.a", "Prototype solutions with documented constraints, tests, and results."),
    isteItem("HS—9–10", "ISTE.ID.HS-9-10.b", "Incorporate usability testing and accessibility checks; iterate."),
    isteItem("HS—9–10", "ISTE.ID.HS-9-10.c", "Justify design decisions using criteria, data, and user feedback."),
  ],
  "Computational Thinker": [
    isteItem("HS—9–10", "ISTE.CT.HS-9-10.a", "Create and debug algorithms; apply decomposition and abstraction."),
    isteItem("HS—9–10", "ISTE.CT.HS-9-10.b", "Use data operations (joins, filters, aggregations) to derive insights."),
    isteItem("HS—9–10", "ISTE.CT.HS-9-10.c", "Automate steps or scripts to optimize tasks; document limitations."),
  ],
  "Creative Communicator": [
    isteItem("HS—9–10", "ISTE.CC.HS-9-10.a", "Produce compelling, audience-centered multimedia with clear narrative."),
    isteItem("HS—9–10", "ISTE.CC.HS-9-10.b", "Adapt content for accessibility and platforms; iterate from feedback."),
    isteItem("HS—9–10", "ISTE.CC.HS-9-10.c", "License/share work appropriately; attribute collaborators and sources."),
  ],
  "Global Collaborator": [
    isteItem("HS—9–10", "ISTE.GC.HS-9-10.a", "Lead collaboration with roles, timelines, and version control in shared spaces."),
    isteItem("HS—9–10", "ISTE.GC.HS-9-10.b", "Engage cross-culturally with empathy and professional netiquette."),
    isteItem("HS—9–10", "ISTE.GC.HS-9-10.c", "Integrate diverse input into a cohesive, high-quality group deliverable."),
  ],
};

const HS_11_12 = {
  "Empowered Learner": [
    isteItem("HS—11–12", "ISTE.EL.HS-11-12.a", "Optimize learning with advanced toolchains and personal knowledge systems."),
    isteItem("HS—11–12", "ISTE.EL.HS-11-12.b", "Evaluate learning strategies with evidence; mentor peers on effective practices."),
    isteItem("HS—11–12", "ISTE.EL.HS-11-12.c", "Curate a portfolio demonstrating growth, reflection, and impact."),
  ],
  "Digital Citizen": [
    isteItem("HS—11–12", "ISTE.DC.HS-11-12.a", "Audit and improve your digital footprint, privacy, and security posture."),
    isteItem("HS—11–12", "ISTE.DC.HS-11-12.b", "Analyze ethical and societal impacts of technology; propose responsible actions."),
    isteItem("HS—11–12", "ISTE.DC.HS-11-12.c", "Apply and advocate for lawful, equitable, and inclusive tech use."),
  ],
  "Knowledge Constructor": [
    isteItem("HS—11–12", "ISTE.KC.HS-11-12.a", "Conduct rigorous inquiry that triangulates scholarly and public sources."),
    isteItem("HS—11–12", "ISTE.KC.HS-11-12.b", "Produce well-supported products that reconcile conflicting evidence."),
    isteItem("HS—11–12", "ISTE.KC.HS-11-12.c", "Use citation managers and consistent styles across formats."),
  ],
  "Innovative Designer": [
    isteItem("HS—11–12", "ISTE.ID.HS-11-12.a", "Deliver refined solutions balancing feasibility, ethics, and user needs."),
    isteItem("HS—11–12", "ISTE.ID.HS-11-12.b", "Conduct and document usability/accessibility testing; implement fixes."),
    isteItem("HS—11–12", "ISTE.ID.HS-11-12.c", "Critically evaluate design tradeoffs and defend final choices."),
  ],
  "Computational Thinker": [
    isteItem("HS—11–12", "ISTE.CT.HS-11-12.a", "Design modular, scalable algorithms; compare implementations."),
    isteItem("HS—11–12", "ISTE.CT.HS-11-12.b", "Analyze larger datasets; communicate valid inferences and uncertainty."),
    isteItem("HS—11–12", "ISTE.CT.HS-11-12.c", "Automate complex workflows; document reproducible processes."),
  ],
  "Creative Communicator": [
    isteItem("HS—11–12", "ISTE.CC.HS-11-12.a", "Create polished communications tailored to expert and public audiences."),
    isteItem("HS—11–12", "ISTE.CC.HS-11-12.b", "Integrate accessibility and UX best practices into final products."),
    isteItem("HS—11–12", "ISTE.CC.HS-11-12.c", "Publish, license, and attribute work professionally."),
  ],
  "Global Collaborator": [
    isteItem("HS—11–12", "ISTE.GC.HS-11-12.a", "Coordinate complex collaborations across time zones/platforms with clear governance."),
    isteItem("HS—11–12", "ISTE.GC.HS-11-12.b", "Facilitate inclusive decision-making; resolve conflicts using evidence and empathy."),
    isteItem("HS—11–12", "ISTE.GC.HS-11-12.c", "Deliver capstone-level products reflecting multiple perspectives and high standards."),
  ],
};

// ----------------------------------
// Export full subject map
// ----------------------------------
export const ISTE_STANDARDS = {
  "ISTE (Students)": {
    // Elementary bands
    "Kindergarten": K2,
    "1st Grade": K2,
    "2nd Grade": K2,

    "3rd Grade": G3_5,
    "4th Grade": G3_5,
    "5th Grade": G3_5,

    // Middle school band
    "6th Grade": G6_8,
    "7th Grade": G6_8,
    "8th Grade": G6_8,

    // High School per-domain clusters
    "High School": {
      "Empowered Learner": {
        "HS—9–10": HS_9_10["Empowered Learner"],
        "HS—11–12": HS_11_12["Empowered Learner"],
      },
      "Digital Citizen": {
        "HS—9–10": HS_9_10["Digital Citizen"],
        "HS—11–12": HS_11_12["Digital Citizen"],
      },
      "Knowledge Constructor": {
        "HS—9–10": HS_9_10["Knowledge Constructor"],
        "HS—11–12": HS_11_12["Knowledge Constructor"],
      },
      "Innovative Designer": {
        "HS—9–10": HS_9_10["Innovative Designer"],
        "HS—11–12": HS_11_12["Innovative Designer"],
      },
      "Computational Thinker": {
        "HS—9–10": HS_9_10["Computational Thinker"],
        "HS—11–12": HS_11_12["Computational Thinker"],
      },
      "Creative Communicator": {
        "HS—9–10": HS_9_10["Creative Communicator"],
        "HS—11–12": HS_11_12["Creative Communicator"],
      },
      "Global Collaborator": {
        "HS—9–10": HS_9_10["Global Collaborator"],
        "HS—11–12": HS_11_12["Global Collaborator"],
      },
    },
  },
};

export default ISTE_STANDARDS;
